import { useAuthStore } from '../stores/auth-store';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Helper to get full document URL (handles both dev and prod)
export function getDocumentUrl(relativeUrl: string): string {
  // If the URL starts with /api, replace it with the API_BASE
  if (relativeUrl.startsWith('/api')) {
    // Remove /api prefix from relative URL since API_BASE already ends with /api
    const path = relativeUrl.substring(4); // Remove '/api'
    return `${API_BASE}${path}`;
  }
  // If it's already a full URL or doesn't start with /api, return as-is
  return relativeUrl;
}

// Helper to download a document with authentication
export async function downloadDocument(url: string, filename: string): Promise<void> {
  const token = await useAuthStore.getState().getAccessToken();
  const fullUrl = getDocumentUrl(url);

  const response = await fetch(fullUrl, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download: ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  // Create a temporary link and click it to trigger download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  URL.revokeObjectURL(blobUrl);
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await useAuthStore.getState().getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Fetch wrapper that automatically retries once on 401 with a refreshed token.
 * Avoids forcing a full re-login for transient token expiry.
 */
async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, init);

  if (response.status === 401) {
    console.warn('[API] 401 received, attempting silent token refresh and retry');
    const freshToken = await useAuthStore.getState().getAccessToken();
    if (freshToken) {
      // Retry the request with the fresh token
      const retryHeaders = new Headers(init?.headers);
      retryHeaders.set('Authorization', `Bearer ${freshToken}`);
      return fetch(url, { ...init, headers: retryHeaders });
    }
    // getAccessToken already handles popup fallback and forceReauthenticate
  }

  return response;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // If we still get 401 after retry, the session is truly expired
    useAuthStore.getState().forceReauthenticate();
    throw new Error('Session expired. Redirecting to login...');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Network error',
      message: response.statusText,
    }));

    // Handle various error formats
    let errorMessage = 'An error occurred';

    if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (errorData.message && typeof errorData.message === 'string') {
      errorMessage = errorData.message;
    } else if (errorData.error && typeof errorData.error === 'string') {
      errorMessage = errorData.error;
    } else if (errorData.error?.issues) {
      // Zod validation error format
      errorMessage = errorData.error.issues.map((i: { message: string }) => i.message).join(', ');
    } else if (Array.isArray(errorData.issues)) {
      // Direct Zod issues array
      errorMessage = errorData.issues.map((i: { message: string }) => i.message).join(', ');
    }

    throw new Error(errorMessage);
  }
  return response.json();
}

/**
 * Handle streaming responses that use keepalive whitespace to prevent Azure timeout.
 * Reads the full text body, trims keepalive spaces, parses JSON, and throws on success: false.
 */
async function handleStreamingResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    useAuthStore.getState().forceReauthenticate();
    throw new Error('Session expired. Redirecting to login...');
  }
  const text = await response.text();
  const data = JSON.parse(text.trim());
  if (data && data.success === false) {
    throw new Error(data.error || 'An error occurred');
  }
  return data as T;
}

// Types - Search Phase (Phase 2)
export type SearchPhaseStatus = 'NOT_STARTED' | 'READY' | 'RUNNING' | 'COMPLETED' | 'EVALUATED';
export type SearchExecutionStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'TIMEOUT';

export interface SearchConfig {
  topN: number;
  searchModels: string[];
}

export interface SearchAutoScore {
  geographic: number;
  specialty: number;
  criteria: number;
  diversity: number;
  relevance: number;
  overall: number;
  details?: Record<string, unknown>;
}

export interface SearchScores {
  relevance: {
    overallRelevance: number;
    specialtyMatch: number;
    criteriaAlignment: number;
    geographicFit: number;
  };
  quality: {
    resultDiversity: number;
    informationCompleteness: number;
    sourceQuality: number;
    clinicalRelevance: number;
  };
  completeness: {
    requiredFieldsCovered: number;
    optionalFieldsCovered: number;
    verifiableInformation: number;
  };
}

export interface SearchExecution {
  id: string;
  executionId: string;
  searchModel: string;
  blindCode: string;
  status: SearchExecutionStatus;
  searchInput?: unknown;
  searchResults?: unknown;
  autoScore?: SearchAutoScore;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  retryCount: number;
}

export interface SearchEvaluationItem {
  id: string;
  evaluationSetId: string;
  searchExecutionId: string;
  blindCode: string;
  verdict: string;
  verdictReasoning?: string;
  scores: SearchScores;
  issues: string[];
  comparisonNotes?: string;
  strengths: string[];
  weaknesses: string[];
  createdAt: string;
}

// Types - General
export interface PatientInfo {
  age?: number;
  gender?: 'male' | 'female' | 'other';
  canton?: string;
  postalCode?: string;
  cityName?: string;
}

export interface CaseDocument {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt: string;
}

export interface Question {
  id: string;
  category: string;
  question: string;
  type: string;
  options?: string[];
  required: boolean;
}

export interface DoctorRecommendation {
  doctorId: string;
  doctorName: string;
  specialty: string;
  score: number;
  reasoning: string;
  matchedCriteria: string[];
  // Additional fields for full display
  title?: string;
  institution?: string;
  city?: string;
  canton?: string;
  expertise?: string;
  website?: string;
}

export interface Recommendation {
  id: string;
  source: 'CLAUDE' | 'CHATGPT' | 'DATABASE' | 'BETTERDOC' | 'GEMINI';
  doctors: DoctorRecommendation[];
  createdAt: string;
}

export interface CaseExecution {
  id: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  completedSteps: string[]; // ["questionnaire", "answers", "indication", "criteria", "recommendations"]
  currentStep: string | null;
  questionnaire: Question[];
  answers: Record<string, string>;
  indication: Record<string, unknown>;
  criteria: Record<string, unknown>;
  recommendations: Recommendation[];
  createdAt: string;
  updatedAt: string;
}

export interface Case {
  id: string;
  title: string;
  condition: string;
  patientInfo: PatientInfo;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  documents: CaseDocument[];
  executions: CaseExecution[];
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string; email: string };
}

// Types - Grand Rounds (Multi-Agent Consensus)
export type GrandRoundsPhase = 'PHASE_1_CRITERIA' | 'PHASE_2_REFERRALS';
export type GrandRoundsStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
export type ConsensusOutcome = 'FULL_CONSENSUS' | 'MAJORITY_CONSENSUS' | 'PARTIAL_CONSENSUS' | 'STABLE_DIVERGENCE';
export type DebateRoundType = 'INITIAL' | 'PEER_REVIEW' | 'RESPONSE' | 'CLARIFICATION' | 'MODERATOR_SYNTHESIS';
export type ContributionType = 'PROPOSAL' | 'CRITIQUE' | 'DEFENSE' | 'CONCESSION' | 'CLARIFICATION' | 'SYNTHESIS';
export type LLMProvider = 'CLAUDE_OPUS_4_5' | 'CLAUDE_OPUS_4_6' | 'GPT_5_2_PRO' | 'GEMINI_3_PRO';

export interface GrandRoundsConfig {
  maxRounds?: number;
  convergenceThreshold?: number;
  stabilityThreshold?: number;
  enablePersonas?: boolean;
  moderatorModel?: LLMProvider;
  moderatorThinkingBudget?: number;
}

export interface ConvergenceMetrics {
  criteriaOverlap: number;
  rankingCorrelation: number;
  positionChanges: number;
  isConverged: boolean;
  isStable: boolean;
  consensusPoints: string[];
  divergencePoints: string[];
}

export interface AgentContribution {
  id: string;
  model: LLMProvider;
  persona?: string | null;
  /** The full prompt sent to the model (from Langfuse) */
  personaPrompt?: string | null;
  isModeratorAgent: boolean;
  contributionType: ContributionType;
  content: Record<string, unknown>;
  confidence?: number | null;
  positionChanged: boolean;
  latencyMs?: number | null;
  costUsd?: number | null;
  tokenUsage?: { input: number; output: number } | null;
  createdAt: string;
}

export interface DebateRound {
  id: string;
  roundNumber: number;
  roundType: DebateRoundType;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  convergenceMetrics?: ConvergenceMetrics;
  startedAt?: string;
  completedAt?: string;
  contributions: AgentContribution[];
}

export interface GrandRoundsSession {
  id: string;
  testRunId: string;
  phase: GrandRoundsPhase;
  status: GrandRoundsStatus;
  outcomeType?: ConsensusOutcome;
  totalRounds: number;
  totalCostUsd: number;
  createdAt: string;
  completedAt?: string;
  rounds: Array<{
    id: string;
    roundNumber: number;
    roundType: DebateRoundType;
    status: string;
    completedAt?: string;
  }>;
}

// Model analysis from Phase 1 executions
export interface ModelAnalysis {
  model: LLMProvider;
  executionId: string;
  indicationAnalysis: Record<string, unknown>;
  criteriaAnalysis: Record<string, unknown>;
}

// Search execution data for Phase 2
export interface SearchExecutionData {
  searchModel: LLMProvider;
  searchInput: Record<string, unknown>;
  searchResults?: Record<string, unknown>;
}

export interface SearchAnalysis {
  model: LLMProvider;
  searchExecutions: SearchExecutionData[];
}

// Input context for Expert View
export interface GrandRoundsInputContext {
  patientInfo?: {
    age?: number;
    gender?: string;
    canton?: string;
    postalCode?: string;
    comorbidities?: string[];
  };
  questionnaire?: Array<{
    id: string;
    question: string;
    type?: string;
    category?: string;
    options?: string[];
  }>;
  answers?: Record<string, string | string[] | number | boolean>;
  documents?: Array<{
    id: string;
    filename: string;
    originalName: string;
    mimeType: string;
  }>;
  modelAnalysis: ModelAnalysis[];
  searchAnalysis?: SearchAnalysis[];
}

export interface GrandRoundsSessionDetail extends GrandRoundsSession {
  testRun: {
    id: string;
    testCase: {
      id: string;
      name: string;
      condition: string;
      patientInfo?: Record<string, unknown>;
      questionnaire?: unknown[];
      answers?: Record<string, unknown>;
    };
  };
  inputContext?: GrandRoundsInputContext;
  config: GrandRoundsConfig;
  consensusResult?: Record<string, unknown>;
  divergentOptions?: Array<Record<string, unknown>>;
  moderatorSummary?: string;
  finalConvergence?: ConvergenceMetrics;
  totalTokens: number;
  startedAt?: string;
  rounds: DebateRound[];
}

export interface GrandRoundsEvent {
  type:
    | 'connected'
    | 'session_started'
    | 'session_completed'
    | 'session_failed'
    | 'session_cancelled'
    | 'round_started'
    | 'round_completed'
    | 'contribution_started'
    | 'contribution_completed'
    | 'contribution_failed'
    | 'moderator_synthesizing'
    | 'convergence_update';
  sessionId?: string;
  roundId?: string;
  roundNumber?: number;
  roundType?: DebateRoundType;
  model?: LLMProvider;
  persona?: string;
  contributionType?: ContributionType;
  positionChanged?: boolean;
  confidence?: number;
  latencyMs?: number;
  outcomeType?: ConsensusOutcome;
  totalRounds?: number;
  totalCostUsd?: number;
  criteriaOverlap?: number;
  positionChanges?: number;
  isConverged?: boolean;
  isStable?: boolean;
  error?: string;
  phase?: GrandRoundsPhase;
  convergence?: {
    criteriaOverlap: number;
    positionChanges: number;
    isConverged: boolean;
    isStable: boolean;
  };
}

export const api = {
  // ============================================================================
  // CASES (Phase 1: Patient info + documents)
  // ============================================================================
  cases: {
    list: async (params?: { limit?: number; offset?: number; status?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.offset) searchParams.set('offset', params.offset.toString());
      if (params?.status) searchParams.set('status', params.status);

      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases?${searchParams}`, { headers });
      return handleResponse<{
        data: Array<
          Case & {
            _count: { comments: number; executions: number };
          }
        >;
        pagination: { total: number; limit: number; offset: number };
      }>(response);
    },

    get: async (id: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${id}`, { headers });
      return handleResponse<{ data: Case }>(response);
    },

    create: async (data: { title: string; condition: string; patientInfo: PatientInfo }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{ data: Case }>(response);
    },

    update: async (
      id: string,
      data: {
        title?: string;
        condition?: string;
        patientInfo?: PatientInfo;
        status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{ data: Case }>(response);
    },

    delete: async (id: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${id}`, {
        method: 'DELETE',
        headers,
      });
      return handleResponse<{ success: boolean }>(response);
    },

    addComment: async (id: string, data: { content: string }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${id}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{ data: Record<string, unknown> }>(response);
    },

    updateComment: async (caseId: string, commentId: string, data: { content: string }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/comments/${commentId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{ data: Record<string, unknown> }>(response);
    },

    deleteComment: async (caseId: string, commentId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/comments/${commentId}`, {
        method: 'DELETE',
        headers,
      });
      return handleResponse<{ success: boolean }>(response);
    },

    uploadDocuments: async (id: string, files: File[]) => {
      const token = await useAuthStore.getState().getAccessToken();
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await authFetch(`${API_BASE}/cases/${id}/documents`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });
      return handleResponse<{ data: CaseDocument[] }>(response);
    },

    deleteDocument: async (caseId: string, documentId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/documents/${documentId}`, {
        method: 'DELETE',
        headers,
      });
      return handleResponse<{ success: boolean }>(response);
    },
  },

  // ============================================================================
  // CASE EXECUTIONS (Phase 2: Workflow runs)
  // ============================================================================
  executions: {
    create: async (caseId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/executions`, {
        method: 'POST',
        headers,
      });
      return handleResponse<{ data: CaseExecution }>(response);
    },

    get: async (caseId: string, executionId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/executions/${executionId}`, {
        headers,
      });
      return handleResponse<{ data: CaseExecution & { case: Case } }>(response);
    },

    update: async (
      caseId: string,
      executionId: string,
      data: {
        questionnaire?: Question[];
        answers?: Record<string, string>;
        indication?: Record<string, unknown>;
        criteria?: Record<string, unknown>;
        completedSteps?: string[];
        currentStep?: string;
        status?: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
        // Search results from all sources
        searchResults?: {
          betterdoc?: Array<Record<string, unknown>>;
          claude?: Array<Record<string, unknown>>;
          chatgpt?: Array<Record<string, unknown>>;
          gemini?: Array<Record<string, unknown>>;
          database?: Array<Record<string, unknown>>;
          searchedAt?: string;
          specialty?: string;
          recommendationId?: string;
          caseId?: string;
        };
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/executions/${executionId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{ data: CaseExecution }>(response);
    },

    saveRecommendations: async (
      caseId: string,
      executionId: string,
      data: {
        source: 'CLAUDE' | 'CHATGPT' | 'DATABASE' | 'BETTERDOC' | 'GEMINI';
        doctors: DoctorRecommendation[];
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/cases/${caseId}/executions/${executionId}/recommendations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );
      return handleResponse<{ data: Recommendation }>(response);
    },

    delete: async (caseId: string, executionId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/cases/${caseId}/executions/${executionId}`, {
        method: 'DELETE',
        headers,
      });
      return handleResponse<{ success: boolean }>(response);
    },
  },

  // ============================================================================
  // ANDY PRO AI ENDPOINTS
  // ============================================================================
  andyPro: {
    generateQuestionnaire: async (data: {
      condition: string;
      patientInfo?: { age?: number; gender?: string; canton?: string };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/questionnaire`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleStreamingResponse<{
        success: boolean;
        data: {
          specialist_role: string;
          recommended_specialties: string[];
          questions: Question[];
        };
        thinking?: string;
      }>(response);
    },

    analyzeIndication: async (data: {
      condition: string;
      patientInfo?: { age?: number; gender?: string; canton?: string };
      questionnaire: Array<{ id: string; question: string; answer: string }>;
      documents?: Array<{ filename?: string; base64Data?: string; originalName: string; mimeType: string }>;
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/indication`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleStreamingResponse<{
        success: boolean;
        data: {
          indication_appropriate: boolean;
          patient_expectations: {
            level: 'realistic' | 'optimistic' | 'unrealistic';
            assessment: string;
          };
          communication_needs: string[];
          provider_traits_needed: string[];
          issues: Array<{ severity: 'critical' | 'warning' | 'info'; description: string }>;
          criteria_adjustments: string[];
        };
        thinking?: string;
      }>(response);
    },

    generateCriteria: async (data: {
      condition: string;
      patientInfo?: { age?: number; gender?: string; canton?: string };
      questionnaire: Array<{ id: string; question: string; answer: string }>;
      indication: Record<string, unknown>;
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/criteria`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleStreamingResponse<{
        success: boolean;
        data: {
          criteria: Array<{
            name: string;
            importance: 'critical' | 'high' | 'medium' | 'low';
            description: string;
          }>;
          search_parameters: {
            specialties: string[];
            min_experience_years: number;
            max_distance_km: number;
            required_capabilities: string[];
          };
          ideal_provider_profile: string;
        };
        thinking?: string;
      }>(response);
    },

    autofill: async (data: {
      condition: string;
      patientInfo?: { age?: number; gender?: string; canton?: string };
      questions: Array<{ id: string; question: string; type: string; options?: string[] }>;
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/autofill`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleStreamingResponse<{
        success: boolean;
        answers: Record<string, string>;
      }>(response);
    },

    generateSampleCase: async () => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/generate-sample-case`, {
        method: 'POST',
        headers,
      });
      return handleResponse<{
        success: boolean;
        data: {
          condition: string;
          patientInfo: {
            age?: number;
            gender?: 'male' | 'female' | 'other';
            canton?: string;
            postalCode?: string;
          };
          suggestedTitle: string;
        };
      }>(response);
    },

    // Doctor Search endpoints
    searchClaude: async (data: {
      condition: string;
      specialty?: string;
      criteria?: Array<{ name: string; importance: string; description?: string }>;
      patientInfo?: { age?: number; gender?: string; canton?: string; postalCode?: string; cityName?: string };
      searchParameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/search-claude`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        webResults: Array<{
          doctorId: string;
          type: string;
          name: string;
          title: string;
          institution: string;
          city: string;
          canton: string;
          specialty: string;
          matchScore: number;
          matchReasons: string[];
          criteriaMatched: string[];
          expertise: string;
          website: string;
          source: string;
        }>;
        searchSummary: string;
        topDoctors: string[];
      }>(response);
    },

    searchGemini: async (data: {
      condition: string;
      specialty?: string;
      criteria?: Array<{ name: string; importance: string; description?: string }>;
      patientInfo?: { age?: number; gender?: string; canton?: string; postalCode?: string };
      searchParameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/search-gemini`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        webResults: Array<{
          doctorId: string;
          type: string;
          name: string;
          title: string;
          institution: string;
          city: string;
          canton: string;
          specialty: string;
          matchScore: number;
          matchReasons: string[];
          criteriaMatched: string[];
          expertise: string;
          website: string;
          source: string;
        }>;
        searchSummary: string;
        topDoctors: string[];
        topInstitutions: string[];
        rankingRationale: string;
      }>(response);
    },

    searchChatgpt: async (data: {
      condition: string;
      specialty?: string;
      criteria?: Array<{ name: string; importance: string; description?: string }>;
      patientInfo?: { age?: number; gender?: string; canton?: string; postalCode?: string; cityName?: string };
      searchParameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/search-chatgpt`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        webResults: Array<{
          doctorId: string;
          type: string;
          name: string;
          title: string;
          institution: string;
          city: string;
          canton: string;
          specialty: string;
          matchScore: number;
          matchReasons: string[];
          criteriaMatched: string[];
          expertise: string;
          website: string;
          source: string;
        }>;
        searchSummary: string;
        topDoctors: string[];
        topInstitutions: string[];
        rankingRationale: string;
      }>(response);
    },

    searchDatabase: async (data: {
      condition: string;
      criteria?: Array<{ name: string; importance: string; description?: string }>;
      patientInfo?: { age?: number; gender?: string; canton?: string; postalCode?: string };
      searchParameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/search-database`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        webResults: Array<{
          doctorId: string;
          type: string;
          name: string;
          title: string;
          institution: string;
          city: string;
          canton: string;
          specialty: string;
          matchScore: number;
          matchReasons: string[];
          criteriaMatched: string[];
          expertise: string;
          website: string;
          source: string;
        }>;
        searchSummary: string;
        totalInDatabase: number;
      }>(response);
    },

    searchBetterDoc: async (data: {
      condition: string;
      specialty?: string;
      criteria?: Array<{ name: string; importance: string; description?: string }>;
      patientInfo?: { age?: number; gender?: string; canton?: string; postalCode?: string };
      searchParameters?: { specialties?: string[]; max_distance_km?: number; min_experience_years?: number };
      icd10Codes?: { leadingCondition?: string | null; otherConditions?: string[] };
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/andy-pro/search-betterdoc`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        webResults: Array<{
          doctorId: string;
          type: string;
          name: string;
          title: string;
          institution: string;
          city: string;
          canton: string;
          specialty: string;
          matchScore: number;
          matchReasons: string[];
          criteriaMatched: string[];
          expertise: string;
          website: string;
          source: string;
          zsrNumber?: string;
          glnNumber?: string;
          isVerified?: boolean;
        }>;
        searchSummary: string;
        recommendationId?: string;
        caseId?: string;
        manualSearchNeeded?: boolean;
        notConfigured?: boolean;
        totalFromBetterDoc?: number;
      }>(response);
    },
  },

  // ============================================================================
  // DOCTORS
  // ============================================================================
  doctors: {
    list: async (params?: {
      limit?: number;
      offset?: number;
      specialty?: string;
      canton?: string;
      search?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.offset) searchParams.set('offset', params.offset.toString());
      if (params?.specialty) searchParams.set('specialty', params.specialty);
      if (params?.canton) searchParams.set('canton', params.canton);
      if (params?.search) searchParams.set('search', params.search);

      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/doctors?${searchParams}`, { headers });
      return handleResponse<{
        data: Array<Record<string, unknown>>;
        pagination: { total: number; limit: number; offset: number };
      }>(response);
    },

    get: async (id: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/doctors/${id}`, { headers });
      return handleResponse<{ data: Record<string, unknown> }>(response);
    },

    search: async (criteria: {
      specialties?: string[];
      canton?: string;
      postalCode?: string;
      maxDistanceKm?: number;
      minExperienceYears?: number;
      limit?: number;
    }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/doctors/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify(criteria),
      });
      return handleResponse<{ data: Array<Record<string, unknown>> }>(response);
    },

    stats: async () => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/doctors/stats/summary`, { headers });
      return handleResponse<{
        data: {
          total: number;
          bySpecialty: Array<{ specialty: string; count: number }>;
          byCanton: Array<{ canton: string; count: number }>;
        };
      }>(response);
    },
  },

  // ============================================================================
  // HEALTH
  // ============================================================================
  health: {
    check: async () => {
      const response = await authFetch(`${API_BASE}/health`);
      return handleResponse<{ status: string; timestamp: string; version: string }>(response);
    },
  },

  // ============================================================================
  // VALIDATION - SEARCH PHASE (Phase 2)
  // ============================================================================
  searchPhase: {
    // Get search phase status and details
    getPhase: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/validation/test-runs/${testRunId}/search-phase`, {
        headers,
      });
      return handleResponse<{
        data: {
          status: SearchPhaseStatus;
          config?: SearchConfig;
          models: string[]; // Test run's configured models from Phase 1
          selectedExecutions: Array<{
            id: string;
            blindCode: string;
            model: string;
            iteration: number;
            selectedForSearch: boolean;
            searchSelectionRank?: number;
          }>;
          searchExecutions: SearchExecution[];
          progress: {
            total: number;
            completed: number;
            failed: number;
            running: number;
            pending: number;
          };
        };
      }>(response);
    },

    // Start search phase - selects Top-N executions and creates SearchExecution records
    // searchModels can optionally override the test run config models (to include GRAND_ROUNDS)
    // includeBetterDoc controls whether BetterDoc API is included in matching (default: true)
    start: async (testRunId: string, data: { topN: number; maxResultsPerSearch?: number; searchModels?: string[]; includeBetterDoc?: boolean }) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/validation/test-runs/${testRunId}/search-phase/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handleResponse<{
        success: boolean;
        data: {
          selectedCount: number;
          searchExecutionsCreated: number;
          selectedExecutions: Array<{ id: string; blindCode: string; rank: number }>;
        };
      }>(response);
    },

    // Execute searches for all pending SearchExecutions
    execute: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/validation/test-runs/${testRunId}/search-phase/execute`, {
        method: 'POST',
        headers,
      });
      return handleResponse<{
        success: boolean;
        message: string;
        status: SearchPhaseStatus;
      }>(response);
    },

    // Get blind search results for evaluation
    getResults: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/validation/test-runs/${testRunId}/search-phase/results`, {
        headers,
      });
      return handleResponse<{
        data: {
          testRunId: string;
          isRevealed: boolean;
          searchExecutions: Array<{
            id: string;
            blindCode: string;
            searchModel?: string; // Only if revealed
            status: SearchExecutionStatus;
            searchResults?: unknown;
            autoScore?: SearchAutoScore;
            completedAt?: string;
          }>;
        };
        testCase?: {
          condition: string;
          patientCanton?: string;
          patientInfo?: {
            age?: number;
            gender?: string;
            canton?: string;
          };
          icd10Codes?: {
            leadingCondition?: string;
            leadingConditionName?: string;
            otherConditions?: Array<{ code: string; name: string }> | string[];
          };
        };
      }>(response);
    },

    // Reset search phase - delete all search executions and reset status
    reset: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await authFetch(`${API_BASE}/validation/test-runs/${testRunId}/search-phase/reset`, {
        method: 'POST',
        headers,
      });
      return handleResponse<{
        success: boolean;
        message: string;
      }>(response);
    },
  },

  // ============================================================================
  // VALIDATION - SEARCH EVALUATIONS
  // ============================================================================
  searchEvaluations: {
    // List search evaluation sets for a test run
    list: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets`,
        { headers }
      );
      return handleResponse<{
        data: Array<{
          id: string;
          testRunId: string;
          evaluatorId: string;
          evaluator: { id: string; name: string; email: string };
          evaluatedCodes: string[];
          presentationOrder: string[];
          rankings: string[];
          selectedBest?: string;
          selectionReasoning?: string;
          evaluatorConfidence?: number;
          evaluatorNotes?: string;
          timeSpentMs?: number;
          completedAt?: string;
          createdAt: string;
          _count: { items: number };
        }>;
      }>(response);
    },

    // Create a new search evaluation set
    create: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        }
      );
      return handleResponse<{
        data: {
          id: string;
          testRunId: string;
          evaluatorId: string;
          evaluatedCodes: string[];
          presentationOrder: string[];
          createdAt: string;
        };
      }>(response);
    },

    // Get a single search evaluation set with items
    get: async (testRunId: string, setId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets/${setId}`,
        { headers }
      );
      return handleResponse<{
        data: {
          id: string;
          testRunId: string;
          evaluator: { id: string; name: string; email: string };
          evaluatedCodes: string[];
          presentationOrder: string[];
          rankings: string[];
          selectedBest?: string;
          selectionReasoning?: string;
          evaluatorConfidence?: number;
          evaluatorNotes?: string;
          timeSpentMs?: number;
          completedAt?: string;
          items: SearchEvaluationItem[];
        };
      }>(response);
    },

    // Submit completed evaluation
    complete: async (
      testRunId: string,
      setId: string,
      data: {
        items: Array<{
          blindCode: string;
          verdict: string;
          verdictReasoning?: string;
          scores: SearchScores;
          issues?: string[];
          comparisonNotes?: string;
          strengths?: string[];
          weaknesses?: string[];
        }>;
        rankings: string[];
        selectedBest?: string;
        selectionReasoning?: string;
        evaluatorConfidence?: number;
        evaluatorNotes?: string;
        timeSpentMs?: number;
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets/${setId}/complete`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );
      return handleResponse<{
        success: boolean;
        data: {
          id: string;
          completedAt: string;
          items: SearchEvaluationItem[];
        };
      }>(response);
    },

    // Save progress (without completing)
    saveProgress: async (
      testRunId: string,
      setId: string,
      data: {
        rankings?: string[];
        selectedBest?: string;
        selectionReasoning?: string;
        evaluatorConfidence?: number;
        evaluatorNotes?: string;
        timeSpentMs?: number;
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets/${setId}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify(data),
        }
      );
      return handleResponse<{
        data: {
          id: string;
          rankings: string[];
          selectedBest?: string;
        };
      }>(response);
    },

    // Delete an evaluation set
    delete: async (testRunId: string, setId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-evaluation-sets/${setId}`,
        {
          method: 'DELETE',
          headers,
        }
      );
      return handleResponse<{ success: boolean }>(response);
    },

    // Get consensus metrics
    getConsensus: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/search-consensus`,
        { headers }
      );
      return handleResponse<{
        evaluationCount: number;
        message?: string;
        data: {
          verdictAgreement: Record<string, { verdicts: string[]; agreement: number }>;
          rankingAgreement: Array<{
            evaluator: string;
            rankings: string[];
            selectedBest?: string;
          }>;
          overallAgreement: number;
          blindCodeToModel?: Record<string, string>;
        } | null;
      }>(response);
    },
  },

  // ============================================================================
  // VALIDATION - GRAND ROUNDS (Multi-Agent Consensus)
  // ============================================================================
  grandRounds: {
    // List Grand Rounds sessions for a test run
    list: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/grand-rounds`,
        { headers }
      );
      return handleResponse<{
        success: boolean;
        data: GrandRoundsSession[];
      }>(response);
    },

    // Start a new Grand Rounds session
    start: async (
      testRunId: string,
      data: {
        phase: 'PHASE_1_CRITERIA' | 'PHASE_2_REFERRALS';
        config?: GrandRoundsConfig;
      }
    ) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/grand-rounds`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );
      return handleResponse<{
        success: boolean;
        message: string;
        sessionId?: string;
      }>(response);
    },

    // Get session details with rounds and contributions
    get: async (sessionId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/grand-rounds/${sessionId}`,
        { headers }
      );
      return handleResponse<{
        success: boolean;
        data: GrandRoundsSessionDetail;
      }>(response);
    },

    // Cancel a running session
    cancel: async (sessionId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/grand-rounds/${sessionId}/cancel`,
        {
          method: 'POST',
          headers,
        }
      );
      return handleResponse<{
        success: boolean;
        message: string;
      }>(response);
    },

    // Delete a session (reset failed/completed sessions)
    delete: async (sessionId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/grand-rounds/${sessionId}`,
        {
          method: 'DELETE',
          headers,
        }
      );
      return handleResponse<{
        success: boolean;
        message: string;
      }>(response);
    },

    // Get formatted transcript
    getTranscript: async (sessionId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/grand-rounds/${sessionId}/transcript`,
        { headers }
      );
      return handleResponse<{
        success: boolean;
        data: {
          sessionId: string;
          transcript: string;
          format: string;
        };
      }>(response);
    },

    // Subscribe to session events (SSE)
    // Note: EventSource doesn't support headers, so we pass the token as a query parameter
    subscribeToEvents: async (
      sessionId: string,
      onEvent: (event: GrandRoundsEvent) => void,
      onError?: (error: Error) => void
    ): Promise<() => void> => {
      // Get auth token to pass as query parameter (EventSource doesn't support headers)
      const token = await useAuthStore.getState().getAccessToken();
      const url = new URL(`${API_BASE}/validation/grand-rounds/${sessionId}/events`, window.location.origin);
      if (token) {
        url.searchParams.set('token', token);
      }

      const eventSource = new EventSource(url.toString());

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as GrandRoundsEvent;
          onEvent(data);
        } catch (e) {
          console.error('[GrandRounds] Failed to parse event:', e);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[GrandRounds] EventSource error:', error);
        if (onError) {
          onError(new Error('Connection lost'));
        }
        eventSource.close();
      };

      // Return cleanup function
      return () => {
        eventSource.close();
      };
    },
  },

  // ============================================================================
  // VALIDATION - QUALITY ASSESSMENT (Phase 5)
  // ============================================================================
  qualityAssessment: {
    // Get quality assessment results for a test run (for results page)
    getResults: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/quality-assessment-results`,
        { headers }
      );
      return handleResponse<{
        data: {
          assessmentSets: Array<{
            id: string;
            evaluator: { id: string; name: string; email: string };
            completedAt: string | null;
            evaluatorNotes: string | null;
            topN: number;
            assessments: Array<{
              id: string;
              provider: {
                id: string;
                canonicalName: string;
                type: string;
                city: string | null;
                canton: string | null;
                specialty: string | null;
                recommendedByModels: string[];
                occurrences: number;
              };
              scores: {
                medicalFitScore: number;
                urgencyTimingScore: number;
                therapyPathwayScore: number;
                complexityRiskScore: number;
                outcomeFitScore: number;
                patientCentricityScore: number;
              };
              totalScore: number;
              isGoodMatch: boolean;
              overallAssessment: string | null;
              dimensionReasons: Record<string, string>;
            }>;
          }>;
          // Model-level aggregated results
          modelResults: Array<{
            model: string;
            avgScore: number;
            goodMatchRate: number;
            goodMatchCount: number;
            totalProviders: number;
            avgDimensionScores: {
              medicalFit: number;
              urgencyTiming: number;
              therapyPathway: number;
              complexityRisk: number;
              outcomeFit: number;
              patientCentricity: number;
            };
          }>;
          summary: {
            totalSets: number;
            completedSets: number;
            totalAssessments: number;
            goodMatchCount: number;
            avgScore: number;
            topProvider: {
              id: string;
              canonicalName: string;
              type: string;
              city: string | null;
              canton: string | null;
              specialty: string | null;
              recommendedByModels: string[];
              occurrences: number;
              avgScore: number;
              evaluationCount: number;
              goodMatchRate: number;
            } | null;
            // Winning model (highest avg quality score)
            winningModel: {
              model: string;
              avgScore: number;
              goodMatchRate: number;
              goodMatchCount: number;
              totalProviders: number;
              avgDimensionScores: {
                medicalFit: number;
                urgencyTiming: number;
                therapyPathway: number;
                complexityRisk: number;
                outcomeFit: number;
                patientCentricity: number;
              };
            } | null;
          };
        };
      }>(response);
    },

    // Trigger AI quality assessment for a test run
    triggerAIAssessment: async (testRunId: string, force = false) => {
      const headers = await getAuthHeaders();
      const url = `${API_BASE}/validation/test-runs/${testRunId}/ai-quality-assessment${force ? '?force=true' : ''}`;
      const response = await fetch(url, { method: 'POST', headers });
      return handleResponse<{
        data: {
          assessmentId: string;
          hasDisagreements: boolean;
        };
      }>(response);
    },

    // Get AI quality assessment for a test run
    getAIAssessment: async (testRunId: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/validation/test-runs/${testRunId}/ai-quality-assessment`,
        { headers }
      );
      return handleResponse<{
        data: {
          id: string;
          testRunId: string;
          status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
          model: string;
          modelVersion: string | null;
          promptVersion: string;
          promptHash: string;
          startedAt: string | null;
          completedAt: string | null;
          scores: {
            medicalFit: { score: number; max: number; justification: string | null };
            urgencyTiming: { score: number; max: number; justification: string | null };
            pathwayCoherence: { score: number; max: number; justification: string | null };
            complexityRisk: { score: number; max: number; justification: string | null };
            evidenceQuality: { score: number; max: number; justification: string | null };
            patientCentricity: { score: number; max: number; justification: string | null };
          };
          overall: {
            score: number;
            maxScore: number;
            rating: 'excellent' | 'good' | 'mixed' | 'poor';
            justification: string | null;
          };
          criticalFindings: Array<{
            finding: string;
            whyItMatters: string;
            whereItShowsUp: string;
            recommendedFix: string;
          }> | null;
          unverifiedRisks: Array<{
            risk: string;
            whatToVerifyNext: string;
            impactIfWrong: 'low' | 'medium' | 'high';
          }> | null;
          providerVerification: Array<{
            providerName: string;
            claimsChecked: Array<{
              claim: string;
              evidenceSummary: string;
              confidence: 'high' | 'medium' | 'low';
              sources: string[];
            }>;
            matchAssessment: {
              meetsHardConstraints: boolean;
              meetsMostHighImportanceCriteria: boolean;
              notes: string;
            };
          }> | null;
          evaluation: {
            confidence: 'high' | 'medium' | 'low' | null;
            confidenceReasons: string[];
          };
          disagreement: {
            hasDisagreement: boolean;
            flags: {
              significantDisagreements: Array<{
                dimension: string;
                aiScore: number;
                humanAvgScore: number;
                difference: number;
                isSignificant: boolean;
                aiJustification: string;
              }>;
              overallAlignment: 'aligned' | 'minor_differences' | 'significant_differences';
            } | null;
          };
          metrics: {
            latencyMs: number | null;
            costUsd: number | null;
          };
          error: string | null;
          // Model rankings for comparison across models
          modelRankings: Array<{
            model: string;
            avgScore: number;
            totalProviders: number;
            goodMatchCount: number;
            goodMatchRate: number;
          }>;
          // Individual provider assessments
          providerAssessments: Array<{
            id: string;
            providerId: string;
            providerName: string;
            providerType: string;
            providerLocation: string;
            providerSpecialty: string | null;
            scores: {
              medicalFit: number;
              urgencyTiming: number;
              pathwayCoherence: number;
              complexityRisk: number;
              evidenceQuality: number;
              patientCentricity: number;
            };
            dimensionJustifications: Record<string, string>;
            totalScore: number;
            isGoodMatch: boolean;
            overallAssessment: string | null;
            recommendedByModels: string[];
            recommendationCount: number;
          }>;
          createdAt: string;
          updatedAt: string;
        };
      }>(response);
    },
  },

  // ============================================================================
  // GENERIC HTTP METHODS (for validation and other endpoints)
  // ============================================================================
  get: async <T = unknown>(url: string): Promise<T> => {
    const headers = await getAuthHeaders();
    const response = await fetch(url.startsWith('http') ? url : `${API_BASE.replace('/api', '')}${url}`, {
      headers,
    });
    return handleResponse<T>(response);
  },

  post: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    const headers = await getAuthHeaders();
    const response = await fetch(url.startsWith('http') ? url : `${API_BASE.replace('/api', '')}${url}`, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  patch: async <T = unknown>(url: string, data?: unknown): Promise<T> => {
    const headers = await getAuthHeaders();
    const response = await fetch(url.startsWith('http') ? url : `${API_BASE.replace('/api', '')}${url}`, {
      method: 'PATCH',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse<T>(response);
  },

  delete: async <T = unknown>(url: string): Promise<T> => {
    const headers = await getAuthHeaders();
    const response = await fetch(url.startsWith('http') ? url : `${API_BASE.replace('/api', '')}${url}`, {
      method: 'DELETE',
      headers,
    });
    return handleResponse<T>(response);
  },
};
